import React, { useEffect, useContext } from "react";
import { LoggerContext } from "context/LoggerContext";
import styles from "./PreferencesSelector.module.scss";
import DynamicFontAwesomeIcon from "components/elements/DynamicFontAwesomeIcon";

/**
 * Component for selecting preferences for tickets
 * Handles different selection modes based on required amount
 */
const PreferencesSelector = ({ ticket, onTicketChange, formErrors, onClearError }) => {
	const { logDebug, logError } = useContext(LoggerContext);
	const { availableAgendas = [], preferences = [], requiredAgendaAmount = 0, id } = ticket;

	// Enhanced debug log to help troubleshoot missing agendas
	useEffect(() => {
		logDebug("PreferencesSelector rendering for ticket:", {
			ticketId: id,
			baseItemId: ticket.baseItemId,
			isForOrderer: ticket.isForOrderer,
			availableAgendasCount: (availableAgendas || []).length,
			availableAgendas: availableAgendas || [],
			requiredAmount: requiredAgendaAmount,
			preferences: preferences || []
		});

		// Warning if no agendas but required amount is set
		if ((!availableAgendas || availableAgendas.length === 0) && requiredAgendaAmount > 0) {
			logError("Missing agendas for ticket that requires preferences", {
				ticketId: id,
				baseItemId: ticket.baseItemId,
				requiredAmount: requiredAgendaAmount
			});
		}
	}, [availableAgendas, requiredAgendaAmount, preferences, id, ticket, logDebug, logError]);

	// Force an array for availableAgendas if it's somehow undefined or null
	const agendas = Array.isArray(availableAgendas) ? availableAgendas : [];

	// Early return with warning if no agendas but preferences are required
	if (agendas.length === 0) {
		if (requiredAgendaAmount > 0) {
			return (
				<div className="mt-4 mb-3 p-3 border border-warning rounded">
					<div className="d-flex align-items-center">
						<DynamicFontAwesomeIcon
							iconName="exclamationCircle"
							className="text-warning me-2"
						/>
						<span>Die Themenliste konnte nicht geladen werden. Bitte laden Sie die Seite neu.</span>
					</div>
				</div>
			);
		}
		return null;
	}

	const hasError = formErrors[`ticket_preferences_${id}`];
	const selectedCount = preferences ? preferences.length : 0;
	const isExactlyRequired = selectedCount === requiredAgendaAmount;

	// Handle preference change - maintains exactly the required number of selections
	const handlePreferenceChange = (agenda, isSelected) => {
		let updatedPreferences = [...(preferences || [])];

		if (isSelected) {
			// If selecting a new preference
			if (!updatedPreferences.includes(agenda.title)) {
				// If already at limit, remove the oldest selection
				if (requiredAgendaAmount > 0 && updatedPreferences.length >= requiredAgendaAmount) {
					updatedPreferences.shift(); // Remove first/oldest item
				}
				updatedPreferences.push(agenda.title);
			}
		} else {
			// If deselecting, just remove this preference
			updatedPreferences = updatedPreferences.filter(p => p !== agenda.title);
		}

		// Log preference change for debugging
		logDebug("Preference change:", {
			ticket: id,
			agenda: agenda.title,
			isSelected,
			old: preferences,
			new: updatedPreferences
		});

		// Update the ticket with new preferences
		onTicketChange({
			...ticket,
			preferences: updatedPreferences
		});

		// Clear validation error if we now have the correct number
		if (updatedPreferences.length >= requiredAgendaAmount && hasError) {
			onClearError(`ticket_preferences_${id}`);
		}
	};

	// Format the time string
	const formatTime = (dateStr, timeStr) => {
		try {
			return new Date(dateStr + 'T' + timeStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		} catch (e) {
			return timeStr || '';
		}
	};

	// For single selection (requiredAgendaAmount = 1), use radio buttons with list design
	if (requiredAgendaAmount === 1) {
		return (
			<div className="mt-4 mb-3">
				<div className="d-flex align-items-center mb-3">
					<h6 className="mb-0">
						Bitte wählen Sie Ihr bevorzugtes Thema
					</h6>

					<div className="d-flex align-items-center ms-auto">
						{hasError && (
							<span className="badge bg-danger text-white me-2" id={`pref_help_${id}`}>
								<DynamicFontAwesomeIcon iconName="exclamationTriangle" className="me-1" />
								{hasError}
							</span>
						)}

						<span className={`badge ${selectedCount === requiredAgendaAmount ? 'bg-success' : 'bg-primary-subtle'}`}>
							{selectedCount}/{requiredAgendaAmount}
						</span>
					</div>
				</div>

				<div className={`${styles.listContainer} ${hasError ? 'border border-danger rounded p-2' : ''}`}>
					{agendas.map((agenda, index) => {
						const isSelected = preferences && preferences.includes(agenda.title);
						return (
							<div
								key={`${id}_agenda_${index}`}
								className={`${styles.listItem} d-flex p-3 mb-2 ${isSelected ? styles.active : ''}`}
								onClick={() => handlePreferenceChange(agenda, true)}
								role="radio"
								aria-checked={isSelected}
								tabIndex={0}
								title={`Thema auswählen: ${agenda.title}`}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										handlePreferenceChange(agenda, true);
									}
								}}>
								<input
									type="radio"
									className={styles.hiddenInput}
									name={`preferences_${id}`}
									id={`pref_${id}_${index}`}
									checked={isSelected || false}
									onChange={() => handlePreferenceChange(agenda, true)}
									required={true}
									aria-label={`Thema: ${agenda.title}`}
									aria-describedby={`pref_help_${id}`}
								/>
								<div className={styles.listItemContent}>
									<div className="d-flex justify-content-between align-items-center">
										<div>
											<h6 className="fs-6 mb-1">{agenda.title}</h6>
											{agenda.desc && (
												<div className="small crop-text-1 mb-0" dangerouslySetInnerHTML={{
													__html: agenda.desc
												}}></div>
											)}
										</div>
										<span className="badge bg-light text-dark ms-2">
											{formatTime(agenda.start_date, agenda.start_time)} - {formatTime(agenda.start_date, agenda.end_time)}
										</span>
									</div>
								</div>
								<DynamicFontAwesomeIcon
									iconName="check"
									className={styles.rightCheckIcon}
									aria-hidden="true"
								/>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	// For multiple selections, use checkbox list with visual feedback
	return (
		<div className="mt-4 mb-3">
			<div className="d-flex align-items-center mb-3">
				<h6 className="mb-0">
					Bitte wählen Sie {requiredAgendaAmount} bevorzugte Themen
				</h6>

				<div className="d-flex align-items-center ms-auto">
					{hasError && (
						<span className="badge bg-danger text-white me-2" id={`pref_help_${id}`}>
							<DynamicFontAwesomeIcon iconName="exclamationTriangle" className="me-1" />
							{hasError}
						</span>
					)}

					<span className={`badge ${selectedCount === requiredAgendaAmount ? 'bg-success' : 'bg-primary-subtle'}`}>
						{selectedCount}/{requiredAgendaAmount}
					</span>
				</div>
			</div>

			<div className={`${styles.listContainer} ${isExactlyRequired ? styles.maxReached : ''} ${hasError ? 'border border-danger rounded p-2' : ''}`}>
				{agendas.map((agenda, index) => {
					const isSelected = preferences && preferences.includes(agenda.title);
					const isDisabled = isExactlyRequired && !isSelected;

					return (
						<div
							key={`${id}_agenda_${index}`}
							className={`rounded-3 ${styles.listItem} d-flex p-3 mb-2 ${isSelected ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
							onClick={() => !isDisabled && handlePreferenceChange(agenda, !isSelected)}
							role="checkbox"
							aria-checked={isSelected || false}
							aria-disabled={isDisabled}
							tabIndex={isDisabled ? -1 : 0}
							title={isDisabled ? "Maximale Anzahl an Themen bereits ausgewählt" : `Thema ${isSelected ? 'abwählen' : 'auswählen'}: ${agenda.title}`}
							onKeyDown={(e) => {
								if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
									e.preventDefault();
									handlePreferenceChange(agenda, !isSelected);
								}
							}}>
							<input
								type="checkbox"
								className={styles.hiddenInput}
								id={`pref_${id}_${index}`}
								checked={isSelected || false}
								disabled={isDisabled}
								onChange={(e) => handlePreferenceChange(agenda, e.target.checked)}
								aria-label={`Thema: ${agenda.title}`}
								aria-describedby={`pref_help_${id}`}
							/>
							<div className={styles.listItemContent}>
								<div className="d-flex justify-content-between align-items-center">
									<div>
										<h6 className="fs-6 mb-0 crop-text-1">{agenda.title}</h6>
										{agenda.desc && (
											<div className="small crop-text-1 mb-0" dangerouslySetInnerHTML={{
												__html: agenda.desc
											}}></div>
										)}
									</div>
									<span className="badge bg-light text-dark ms-2">
										{formatTime(agenda.start_date, agenda.start_time)} - {formatTime(agenda.start_date, agenda.end_time)}
									</span>
								</div>

							</div>
							<DynamicFontAwesomeIcon
								iconName="check"
								className={styles.rightCheckIcon}
								aria-hidden="true"
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default PreferencesSelector;
